import {Spec} from 'immutability-helper';
import _ from 'underscore';

import {DataModificationsHeader} from 'audit/DataModifications';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import {DocumentUpdateSpec, EventConstants} from 'constants/EventConstants';
import AppDispatcher from 'lib/Dispatcher';
import * as Metrics from 'metrics/Metrics';
import {
  Document,
  FromServerDocument,
  ToServerDocumentWithMetadata,
} from 'notebook/documents/DocumentConstants';
import DocumentDAO, {DocumentsByVersion} from 'notebook/documents/DocumentDAO';
import DocumentStore from 'notebook/documents/DocumentStore';
import DocumentUtil from 'notebook/documents/DocumentUtil';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import {CidIdMap, CidNotePartProvenanceIdMap} from 'notebook/notes/NoteConstants';
import {ServerDocumentEditorLock} from 'notebook/realtime/DocumentEditorLockConstants';
import AutoSaver from 'notebook/store/auto-save/AutoSaver';
import {ItemSaveHandler} from 'notebook/store/VersionedItemSubstore';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {EvaluationMetrics} from 'notebook/tables/excess/EvaluationConstants';
import {DocumentTableOp, MultidocTableOpMap} from 'notebook/tables/excess/ops/TableOpConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {EntryNote} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {getBucketStringForTag} from 'performance/PerformanceUtils';
import DocumentEditorLockUtil from 'realtime/util/DocumentEditorLockUtil';
import * as net from 'util/Net';
import pick from 'util/pick';

export interface DocumentUpdateOptions {
  structuredTableMappings?: Array<StructuredTableMapping>;
  updatedItemPartIndexes?: Array<number>;
  canIncrementalSave?: boolean;
  performTableEvaluation?: boolean;
  multidocTableOp?: MultidocTableOpMap;
  evalMetrics?: EvaluationMetrics;
}

export interface DocumentOpenOptions {
  // Allows the document to be immediately patched if evaluation runs on open.
  bumpVersion?: boolean;
  // Disables the internal autosaver for this document.
  disableAutoSaver?: boolean;
  // Provides a custom AutoSaver instance to use instead of creating a new one.
  customAutoSaver?: AutoSaver | null;
  // Provides a custom save handler to use instead of the default save behavior.
  customSaveHandler?: ItemSaveHandler;
  /**
   * A function that provides the related document ids for a document. This is important to provide when
   * allowing Cross-Step Data Refs so that there are an initial set of related document ids for
   * the document.
   * */
  relatedDocumentIdGetter?: () => Array<string>;
  /**
   * Document-level operation to include in the multidocTableOp.
   * If not provided and evaluationSnapshot is missing, 'eval-document-missing-snapshot' is automatically used.
   */
  documentOp?: DocumentTableOp;
}

export const LATEST_DOCUMENT_VERSION = 'LATEST_VERSION' as const;
export type HistoricalDocumentVersionId = number | `docver_${string}`;
export type DocumentVersionSelector = typeof LATEST_DOCUMENT_VERSION | HistoricalDocumentVersionId;

export interface DocumentOpenVersionOptions extends DocumentOpenOptions {
  // Explicitly chooses latest or a specific version to open.
  documentVersionId: DocumentVersionSelector;
}

function isHistoricalDocumentVersionId(value: unknown): value is HistoricalDocumentVersionId {
  return typeof value === 'number' || (typeof value === 'string' && value.startsWith('docver_'));
}

export function toDocumentVersionSelector(
  documentVersionId: string | number | null | undefined
): DocumentVersionSelector {
  if (documentVersionId == null) {
    return LATEST_DOCUMENT_VERSION;
  }
  if (documentVersionId === LATEST_DOCUMENT_VERSION) {
    return LATEST_DOCUMENT_VERSION;
  }
  if (isHistoricalDocumentVersionId(documentVersionId)) {
    return documentVersionId;
  }
  throw new Error(
    `Invalid documentVersionId "${documentVersionId}". Expected "${LATEST_DOCUMENT_VERSION}" or a valid version id.`
  );
}

interface DocumentSaveResponseData {
  id: string;
  cidIdMap?: CidIdMap;
  cidNotePartProvenanceIdMap?: CidNotePartProvenanceIdMap;
  lock?: ServerDocumentEditorLock;
}

interface DocumentToSave {
  toServerDocument: ToServerDocumentWithMetadata & {id: string};
  openVersion: number;
}

/**
 * This function handles dispatching the appropriate events after a DocumentDAO.(save|saveMany) response is
 * returned. It handles dispatching the appropriate signals for each of the Documents to the store. It is
 * intended to be used as part of a Promise chain.
 */
function processDocumentSaveResponse(
  documentsToSave: Array<DocumentToSave>,
  docResponse: DocumentSaveResponseData
): void {
  processDocumentSaveResponses(documentsToSave, [docResponse]);
}

/**
 * Batch version of processDocumentSaveResponse that handles multiple responses in a single dispatch.
 */
function processDocumentSaveResponses(
  documentsToSave: Array<DocumentToSave>,
  docResponses: Array<DocumentSaveResponseData>
): void {
  const saveCompletedPayloads: Array<{
    documentId: string;
    savedVersion: number;
    versionId: string | number | undefined;
    cidIdMap: CidIdMap | undefined;
    cidNotePartProvenanceIdMap: CidNotePartProvenanceIdMap | undefined;
    lock: ServerDocumentEditorLock | undefined;
  }> = [];

  for (const docResponse of docResponses) {
    const documentId = docResponse.id;
    const documentData = documentsToSave.find((doc) => doc.toServerDocument.id === documentId);

    if (documentData) {
      saveCompletedPayloads.push({
        documentId,
        savedVersion: documentData.openVersion,
        versionId: docResponse.cidIdMap?.[documentData.toServerDocument.documentVersion.cid],
        cidIdMap: docResponse.cidIdMap,
        cidNotePartProvenanceIdMap: docResponse.cidNotePartProvenanceIdMap,
        lock: docResponse.lock,
      });

      if (DocumentStore.inStore(documentId)) {
        DocumentActions.fetchVersions(documentId);
      }
    }
  }

  if (saveCompletedPayloads.length > 0) {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_SAVE_COMPLETED_MANY,
      payloads: saveCompletedPayloads,
    });
  }
}

/**
 * This is a Promise failure handler for any Document save actions. Its job is to show a friendly error as a
 * toast and then dispatch an action for each of the DocumentIds provided that the save failed.  This should
 * be the final `fail` handler on the Promise chain.
 */
function handleSaveFailure(
  deferredPromise: JQueryDeferred<boolean>,
  documentIds: Array<string>,
  response: Response
): void {
  const friendlyMessage = notifications.getFriendlyError(response);
  if (friendlyMessage != null) {
    notifications.error(friendlyMessage, {id: 'document-save-fail', duration: 0});
  }
  documentIds.forEach((documentId) => {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_SAVE_FAILED,
      documentId,
      error: response,
    });
  });
  // Resolve the deferred promise to signal that the save has failed.
  deferredPromise.resolve(false);
}

function _invokeCustomSaveHandler(
  customSaveHandler: ItemSaveHandler,
  dataModsHeader: DataModificationsHeader | undefined,
  deferredPromise: JQueryDeferred<boolean>
): void {
  customSaveHandler(dataModsHeader)
    .then((success) => {
      // Resolve the deferred promise to signal that the save has completed.
      deferredPromise.resolve(success);
    })
    .catch((error) => {
      notifications.notifyError(error);
      // Resolve the deferred promise to signal that the save has failed.
      deferredPromise.resolve(false);
    });
}

/**
 * Private helper that performs a throttled save for a single document.
 */
function _performThrottledDocumentSave(
  documentId: string,
  dataModsHeaders: DataModificationsHeader | undefined,
  deferredPromise: JQueryDeferred<boolean>
): void {
  // This is saved here in case the document is removed from store before save finishes.
  const dataAtSaveTime = DocumentStore.getToServerDocument(documentId);

  benchlingUtil.asyncThrottle(`document-save-${documentId}`, () => {
    // Remove from tracking since execution has started
    // If a new save is called now, it will create a new promise
    delete pendingSavePromises[documentId];

    let dataToUse;
    if (DocumentStore.inStore(documentId)) {
      if (!DocumentStore.hasUnsavedChanges(documentId)) {
        return deferredPromise.resolve(false).promise();
      }
      dataToUse = DocumentStore.getToServerDocument(documentId);
    } else {
      dataToUse = dataAtSaveTime;
    }

    const {toServerDocument} = dataToUse;
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_SAVE,
      documentId,
    });
    const xhr = DocumentDAO.update(documentId, toServerDocument, dataModsHeaders)
      .then((response) => {
        if (!response) {
          return false;
        }

        processDocumentSaveResponse(
          [{...dataToUse, toServerDocument: {...toServerDocument, id: documentId}}],
          {
            ...response,
            id: documentId,
          }
        );
        DocumentEditorLockUtil.releaseLocks([documentId]);
        return true;
      })
      .done((success) => {
        deferredPromise.resolve(success);
      })
      .fail((response) => {
        handleSaveFailure(deferredPromise, [documentId], response);
      });
    return xhr;
  });
}

// Track pending save promises for each document to reuse them if the save hasn't started yet.
// We remove the promise from tracking when the callback executes, so if a new save is called
// after execution starts, it will create a new promise.
const pendingSavePromises: {[documentId: string]: JQueryDeferred<boolean> | undefined} = {};

function getOrCreateDeferredPromise(namespace: string): JQueryDeferred<boolean> {
  if (!pendingSavePromises[namespace]) {
    pendingSavePromises[namespace] = $.Deferred<boolean>();
  }
  return pendingSavePromises[namespace];
}

function applyOpenOptionsToInStoreDocuments(
  documentIds: Array<string>,
  {disableAutoSaver, customSaveHandler}: Pick<DocumentOpenOptions, 'disableAutoSaver' | 'customSaveHandler'>
): void {
  for (const documentId of documentIds) {
    if (!DocumentStore.inStore(documentId)) {
      continue;
    }
    if (disableAutoSaver) {
      DocumentStore.disableAutoSaver(documentId);
    }
    if (customSaveHandler) {
      DocumentStore.setCustomSaveHandler(documentId, customSaveHandler);
    }
  }
}

const DocumentActions = {
  close(documentId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_CLOSE,
      documentId,
    });
  },

  // Returns true if the document is successfully fetched from the server, false if the server returns an error.
  async open(documentId: string, options: DocumentOpenVersionOptions): Promise<boolean> {
    const {
      bumpVersion,
      disableAutoSaver,
      customAutoSaver,
      customSaveHandler,
      relatedDocumentIdGetter,
      documentOp,
      documentVersionId,
    } = options ?? {documentVersionId: LATEST_DOCUMENT_VERSION};
    const normalizedDocumentVersionId = toDocumentVersionSelector(documentVersionId);
    const shouldOpenLatestVersion = normalizedDocumentVersionId === LATEST_DOCUMENT_VERSION;
    // Cross-step refs: delegate to openMany (not applicable when loading a specific version)
    if (shouldOpenLatestVersion && BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS) {
      const effectiveRelatedDocumentIdGetter =
        relatedDocumentIdGetter || (() => DocumentStore.getRelatedDocumentIds(documentId));
      const relatedDocumentIds = effectiveRelatedDocumentIdGetter();
      if (relatedDocumentIds.length > 0) {
        const orderedDocumentIds = [documentId, ...relatedDocumentIds.filter((id) => id !== documentId)];
        return DocumentActions.openMany(orderedDocumentIds, {
          bumpVersion,
          disableAutoSaver,
          customAutoSaver,
          customSaveHandler,
          relatedDocumentIdGetter,
          documentOp,
        });
      }
    }

    if (shouldOpenLatestVersion) {
      applyOpenOptionsToInStoreDocuments([documentId], {disableAutoSaver, customSaveHandler});
    }

    let didSucceed = false;
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_OPEN,
      documentId,
      disableAutoSaver: shouldOpenLatestVersion ? disableAutoSaver : true,
      customAutoSaver: shouldOpenLatestVersion ? customAutoSaver : undefined,
      customSaveHandler: shouldOpenLatestVersion ? customSaveHandler : undefined,
      relatedDocumentIdGetter: shouldOpenLatestVersion ? relatedDocumentIdGetter : undefined,
    });
    try {
      const document = shouldOpenLatestVersion
        ? await Promise.resolve(DocumentDAO.get(documentId))
        : await Promise.resolve(DocumentDAO.getVersion(documentId, normalizedDocumentVersionId));
      AppDispatcher.dispatch({
        actionType: EventConstants.DOCUMENT_OPEN_COMPLETED,
        documentId,
        document,
        bumpVersion: shouldOpenLatestVersion ? bumpVersion : undefined,
      });
      if (shouldOpenLatestVersion && bumpVersion) {
        let multidocTableOp: MultidocTableOpMap | undefined;
        const loadedDocument = DocumentStore.getDocument(documentId);
        if (loadedDocument) {
          const effectiveDocOp =
            documentOp ??
            (TableUtil.isMissingEvaluationSnapshot(loadedDocument.documentVersion.entryVersion.note)
              ? 'eval-document-missing-snapshot'
              : undefined);
          if (effectiveDocOp) {
            multidocTableOp = {[documentId]: effectiveDocOp};
          }
        }
        AppDispatcher.dispatch({
          actionType: EventConstants.DOCUMENT_UPDATE,
          documentId,
          op: {$merge: {}}, // NOOP
          bumpVersion: true,
          multidocTableOp,
        });
      }
      didSucceed = true;
    } catch (err) {
      if (net.isServerRequestError(err)) {
        AppDispatcher.dispatch({
          actionType: EventConstants.DOCUMENT_OPEN_FAILED,
          documentId,
        });
        notifications.notifyError(err);
      } else {
        throw err;
      }
    }
    return didSucceed;
  },

  /**
   * Refreshes a document that already exists in the store.
   *
   * The difference between this and `open` is that this does not initialize the store with the document since
   * it is assumed that the document is already in the store.
   *
   * Returns true if the document is successfully refreshed, false if the server returns an error.
   */
  async refresh(documentId: string): Promise<boolean> {
    // Assert that the document already exists in the store
    if (!DocumentStore.inStore(documentId)) {
      throw new Error(`Document ${documentId} is not in store. Use DocumentActions.open() instead.`);
    }

    let didSucceed = false;

    try {
      const document = await Promise.resolve(DocumentDAO.get(documentId));
      AppDispatcher.dispatch({
        actionType: EventConstants.DOCUMENT_OPEN_COMPLETED,
        documentId,
        document,
      });
      didSucceed = true;
    } catch (err) {
      if (net.isServerRequestError(err)) {
        AppDispatcher.dispatch({
          actionType: EventConstants.DOCUMENT_OPEN_FAILED,
          documentId,
        });
        notifications.notifyError(err);
      } else {
        throw err;
      }
    }
    return didSucceed;
  },

  /**
   * Batch-open multiple documents with a single API call. Mirrors the behaviour of `open` but
   * operates on an array of `documentIds` and utilises `DocumentDAO.getAll` for efficiency.
   *
   * Returns a promise that resolves to `true` when all documents are successfully fetched, or
   * `false` if the request fails.
   */
  async openMany(
    documentIds: Array<string>,
    {
      bumpVersion,
      disableAutoSaver,
      customAutoSaver,
      customSaveHandler,
      relatedDocumentIdGetter,
      documentOp,
    }: DocumentOpenOptions = {}
  ): Promise<boolean> {
    if (documentIds.length === 0) {
      return true;
    }

    applyOpenOptionsToInStoreDocuments(documentIds, {disableAutoSaver, customSaveHandler});

    // Dispatch DOCUMENT_OPEN_MANY to notify the store about all documents being opened.
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_OPEN_MANY,
      documentIds,
      disableAutoSaver,
      customAutoSaver,
      customSaveHandler,
      relatedDocumentIdGetter,
    });

    try {
      const {documents} = await DocumentDAO.getAll(documentIds);
      // The response may not include every requested document (e.g. inaccessible); handle returned set.
      // Do an initial load of the documents without bumping the version or performing table evaluation. This
      // helps to ensure that all the documents are loaded into the store to then make sure that evaluation
      // runs successfully afterward if there are cross-step data refs.
      AppDispatcher.dispatch({
        actionType: EventConstants.DOCUMENT_OPEN_COMPLETED_MANY,
        documents,
        bumpVersion: false,
        performTableEvaluation: false,
      });

      // Generate multidocTableOp with document-level operations for documents that need evaluation
      let multidocTableOp: MultidocTableOpMap | undefined;
      for (const docId of documentIds) {
        const loadedDocument = DocumentStore.getDocument(docId);
        if (loadedDocument) {
          // Use explicit documentOp if provided, otherwise auto-detect missing evaluation snapshot
          const effectiveDocOp =
            documentOp ??
            (TableUtil.isMissingEvaluationSnapshot(loadedDocument.documentVersion.entryVersion.note)
              ? 'eval-document-missing-snapshot'
              : undefined);
          if (effectiveDocOp) {
            if (!multidocTableOp) {
              multidocTableOp = {};
            }
            multidocTableOp[docId] = effectiveDocOp;
          }
        }
      }
      // Then after everything is loaded into the store, we can perform Table Evaluation if it is needed
      // because of missing evaluation snapshots and bump the version if specified.
      if (multidocTableOp) {
        AppDispatcher.dispatch({
          actionType: EventConstants.DOCUMENT_UPDATE_MANY,
          documents,
          op: {$merge: {}},
          bumpVersion: bumpVersion ?? false,
          performTableEvaluation: true,
          multidocTableOp,
        });
      }
      return true;
    } catch (err) {
      if (net.isServerRequestError(err)) {
        // Emit failure for each document so any listeners can react accordingly.
        AppDispatcher.dispatch({
          actionType: EventConstants.DOCUMENT_OPEN_FAILED_MANY,
          documentIds,
        });
        notifications.notifyError(err, {extra: 'Failed to load all documents, please refresh the page.'});
        return false;
      }
      throw err; // Non-server failure – rethrow.
    }
  },

  save(documentId: string, dataModsHeaders?: DataModificationsHeader): JQueryPromise<boolean> {
    // If a save handler is provided to the Document ex: Via StepConfigEditor, use the save provided
    // rather than utilizing the Document PATCH route
    const customSaveHandler = DocumentStore.getCustomSaveHandler(documentId);

    if (customSaveHandler) {
      const deferredPromise = $.Deferred<boolean>();
      // For custom save handlers, always create a new promise since the call is not throttled here. It is
      // the custom save handler's responsibility to throttle/serialize if necessary.
      _invokeCustomSaveHandler(customSaveHandler, dataModsHeaders, deferredPromise);
      return deferredPromise.promise();
    } else {
      // Normal throttled single-document save
      // Check if there's a pending promise that hasn't started executing yet. If so, reuse it since
      // asyncThrottle will discard the previous callback. Even though the callback is discarded, we still want
      // the promise returned to the caller to resolve when the superseding request resolves.
      const deferredPromise = getOrCreateDeferredPromise(documentId);
      _performThrottledDocumentSave(documentId, dataModsHeaders, deferredPromise);

      // Return the deferred promise so that the caller can wait for the save to complete.
      return deferredPromise.promise();
    }
  },

  saveMany(
    documentIds: Array<string>,
    parentId?: string,
    dataModsHeader?: DataModificationsHeader
  ): JQueryPromise<boolean> {
    const namespace = parentId ? `document-save-many-${parentId}` : 'document-save-many';
    const deferredPromise = getOrCreateDeferredPromise(namespace);

    benchlingUtil.asyncThrottle(namespace, () => {
      delete pendingSavePromises[namespace];
      // Filter to only documents that have unsaved changes and are in the store
      const documentsToSave = documentIds
        .filter(
          (documentId) => DocumentStore.inStore(documentId) && DocumentStore.hasUnsavedChanges(documentId)
        )
        .flatMap((documentId) => {
          const toServerDocument = DocumentStore.getToServerDocument(documentId);
          if (!toServerDocument) {
            return [];
          }
          return [
            {
              toServerDocument: {
                id: documentId,
                ...toServerDocument.toServerDocument,
              },
              openVersion: toServerDocument.openVersion, // Keep for local use in response handling
            },
          ];
        });

      // Prepare data for server - exclude openVersion field as it's not expected by the API
      const documentsForServer: Array<
        ToServerDocumentWithMetadata & {
          id: string;
        }
      > = documentsToSave.map(({toServerDocument}) => toServerDocument);

      if (documentsToSave.length === 0) {
        return deferredPromise.resolve(true).promise();
      }

      // Dispatch save start events for all documents
      AppDispatcher.dispatch({
        actionType: EventConstants.DOCUMENT_SAVE_MANY,
        documentIds: documentsForServer.map(({id}) => id),
      });

      const saveStartTime = performance.now();

      DocumentDAO.bulkUpdate(documentsForServer, dataModsHeader)
        .then((response) => {
          const totalSaveTime = performance.now() - saveStartTime;

          Metrics.distribution('worksheet.bulk_save_time_ms', totalSaveTime, {
            doc_count: getBucketStringForTag(documentsToSave.length, [5, 10, 15, 20, 30, 40, 50]),
          });

          if (!response?.responses) {
            return false;
          }
          processDocumentSaveResponses(documentsToSave, response.responses);
          DocumentEditorLockUtil.releaseLocks(DocumentStore.getRelatedDocumentIds(documentIds[0]));
          return true;
        })
        .done((success) => {
          // Resolve the deferred promise to signal that the save has completed.
          deferredPromise.resolve(success);
        })
        .fail((response) => {
          handleSaveFailure(deferredPromise, documentIds, response);
        });

      return deferredPromise.promise();
    });
    return deferredPromise.promise();
  },

  registerObjects(documentId: string, cidIdMap: CidIdMap): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_REGISTER_OBJECTS,
      documentId,
      cidIdMap,
    });
  },

  /**
   * Sets a name override for a document. This is necessary for Documents in Procedures Config that don't
   * have a real parent document name set. This should be called before the document is opened, and called
   * when the document is "renamed".
   */
  registerNameOverride(documentId: string, nameOverride: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_REGISTER_NAME_OVERRIDE,
      documentId,
      nameOverride,
    });
  },

  /** Directly updates the note of a given Document.
   *
   * If Table Mappings are provided, they will overwrite the existing ones.
   */
  updateNote(
    documentId: string,
    note: EntryNote,
    {
      structuredTableMappings,
      updatedItemPartIndexes,
      canIncrementalSave,
      performTableEvaluation,
      multidocTableOp,
    }: DocumentUpdateOptions = {}
  ): void {
    DocumentActions.update(
      documentId,
      DocumentUtil.entryVersionUpdate({
        $merge: {
          note,
          ...(structuredTableMappings && {
            structuredTableMappings,
          }),
        },
      }),
      {
        updatedItemPartIndexes,
        cidIdMap: null,
        bumpVersion: true,
        performTableEvaluation,
        canIncrementalSave,
        multidocTableOp,
      }
    );
  },

  setName(documentId: string, name: string): void {
    DocumentActions.update(documentId, {$merge: {name}});
  },

  /**
   * Revert the document to a previous state
   */
  revert: (documentId: string, op: Spec<Document>, restoreData: SelectionRestoreData): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_REVERT,
      documentId,
      op,
      restoreData,
      multidocTableOp: {[documentId]: 'eval-document'},
    });
  },

  loadVersion(documentId: string, versionId: number): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_LOAD_VERSION,
      documentId,
      versionId,
    });
    DocumentDAO.getVersion(documentId, versionId).then(
      (response: FromServerDocument) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.DOCUMENT_LOAD_VERSION_COMPLETED,
          documentId,
          versionId,
          version: response,
        });
      },
      () => {
        AppDispatcher.dispatch({
          actionType: EventConstants.DOCUMENT_LOAD_VERSION_FAILED,
          documentId,
          versionId,
        });
      }
    );
  },

  fetchVersions(documentId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_FETCH_VERSIONS,
      documentId,
    });
    if (DocumentStore.inStore(documentId)) {
      DocumentDAO.getVersions(documentId).then(
        (response: DocumentsByVersion) => {
          AppDispatcher.dispatch(
            // eslint-disable-next-line no-restricted-syntax
            _.extend(
              {
                actionType: EventConstants.DOCUMENT_FETCH_VERSIONS_COMPLETED,
                documentId,
                versions: response.data,
              },
              pick(response, 'parent')
            )
          ); // parent key present with null value: the parent was deleted
          // omitted parent key: there is no parent.
        },
        () => {
          AppDispatcher.dispatch({
            actionType: EventConstants.DOCUMENT_FETCH_VERSIONS_FAILED,
            documentId,
          });
        }
      );
    } else {
      AppDispatcher.dispatch({
        actionType: EventConstants.DOCUMENT_FETCH_VERSIONS_FAILED,
        documentId,
      });
    }
  },

  /**
   * Applies `op` to the document with id `documentId`, and merges in the specified `cidIdMap`.
   * If `op` introduces new models, it should specify the ids in `cidIdMap`.
   */
  update(
    documentId: string,
    op: Spec<Document>,
    param?: {
      cidIdMap: CidIdMap | null;
      bumpVersion: boolean | null;
      updatedItemPartIndexes?: Array<number>;
      canIncrementalSave?: boolean;
      performTableEvaluation?: boolean;
      multidocTableOp?: MultidocTableOpMap;
      evalMetrics?: EvaluationMetrics;
    }
  ): void {
    // Eager update the client's lock state prior to persisting the new document version which will lock the documents
    DocumentEditorLockUtil.acquireLocks(documentId);

    if (param == null) {
      param = {cidIdMap: null, bumpVersion: null};
    }
    const {cidIdMap, bumpVersion} = param;
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_UPDATE,
      documentId,
      op,
      cidIdMap: cidIdMap != null ? cidIdMap : {},
      bumpVersion: bumpVersion != null ? bumpVersion : true,
      updatedItemPartIndexes: param?.updatedItemPartIndexes,
      canIncrementalSave: param?.canIncrementalSave,
      performTableEvaluation: param?.performTableEvaluation,
      multidocTableOp: param?.multidocTableOp,
      evalMetrics: param?.evalMetrics,
    });
  },

  /**
   * Updates the document with the given spec and also updates all related documents
   * @param documentUpdate The update to the main document
   * @param relatedDocumentUpdates An array of updates to related documents
   * @param options
   */
  updateDocumentGroup: (
    documentUpdate: DocumentUpdateSpec,
    relatedDocumentUpdates?: Array<DocumentUpdateSpec>,
    options?: DocumentUpdateOptions
  ): void => {
    const {id: documentId} = documentUpdate;
    // Eager update client's lock state prior to persisting the new document versions which will lock the documents
    DocumentEditorLockUtil.acquireLocks(documentId);
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_UPDATE_RELATED_DOCUMENTS,
      documentId,
      documentUpdate,
      relatedDocumentUpdates,
      options,
    });
  },

  postServerUpdate: (documentId: string, data: Partial<Document>): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_UPDATE,
      documentId,
      op: {$merge: data},
      bumpVersion: false,
    });
  },

  postServerSideUpdate: (documentId: string): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_SERVER_SIDE_UPDATE,
      documentId,
      bumpVersion: false,
    });
  },

  invalidate(documentId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_INVALIDATE,
      documentId,
    });
  },

  revalidate: (documentId: string): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_OPEN_FAILED,
      documentId,
      // Since revalidate is called when an ActionFunction is aborted, the document will not be out of date,
      // unlike when we dispatch the same event when failing to reload or open the document.
      clearDirtyState: true,
    });
  },

  deleteDocument: (documentId: string): void => {
    const relatedDocumentIds = DocumentStore.getRelatedDocumentIds(documentId);
    DocumentActions.close(documentId);
    for (const relatedDocumentId of relatedDocumentIds) {
      const relatedDocumentIdsWithoutDeletedDocument = DocumentStore.getRelatedDocumentIds(
        relatedDocumentId
      ).filter((id) => id !== documentId);
      // Ensure that the deleted document is removed from the related document ids.
      DocumentStore.setRelatedDocumentIdGetter(
        relatedDocumentId,
        () => relatedDocumentIdsWithoutDeletedDocument
      );
    }
    // If there are any related documents, reevaluate one of them to trigger a reevaluation of all the
    // related documents.
    if (relatedDocumentIds.length > 0) {
      const multidocTableOp: MultidocTableOpMap = {
        [documentId]: 'delete-document',
      };
      for (const relatedDocumentId of relatedDocumentIds) {
        multidocTableOp[relatedDocumentId] = 'eval-document';
      }
      DocumentActions.forceEvaluation(relatedDocumentIds[0], {bumpVersion: true, multidocTableOp});
    }
  },

  forceEvaluation: (
    fileId: string,
    {bumpVersion = false, multidocTableOp}: {bumpVersion?: boolean; multidocTableOp?: MultidocTableOpMap} = {}
  ): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.EXCESS_FORCE_EVALUATION,
      fileId,
      bumpVersion,
      multidocTableOp,
    });
  },

  notifySaveStart: (updates: Array<{documentId: string}>): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_SAVE_MANY,
      documentIds: updates.map(({documentId}) => documentId),
    });
  },

  notifySaveComplete: (
    updates: Array<{documentId: string; openVersion: number; documentVersionCid: string}>,
    cidIdMap: CidIdMap
  ): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.DOCUMENT_SAVE_COMPLETED_MANY,
      updates,
      cidIdMap,
    });
    const documentsToUnlock = new Set(
      BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS
        ? updates.flatMap(({documentId}) => [documentId, ...DocumentStore.getRelatedDocumentIds(documentId)])
        : updates.map(({documentId}) => documentId)
    );
    DocumentEditorLockUtil.releaseLocks(Array.from(documentsToUnlock));
  },
};

export default DocumentActions;
